import Bowser from "bowser";
const browser = Bowser.getParser(window.navigator.userAgent);
const platform = browser.getPlatform();
export const osName = browser.getOSName() || "Unknown OS";
export const deviceType = platform.type || "Unknown Type"; // desktop or mobile or tablet
export const browserName = browser.getBrowserName() || "Unknown Browser";
export const isApple = ["iOS", "macOS", "watchOS", "tvOS"].includes(osName);
export const isSafari = browserName === "Safari";
export const isDesktop = deviceType === "desktop";
export const isIOS = deviceType === "mobile"; //osName === "iOS";

declare global {
    interface String {
        capitalize(): string;
        titleCase(): string;
        truncate(): string;
        truncate2(limit: number): string;
        toCents(): number;
        isNumeric(): boolean;
    }

    interface Number {
        toCashAmount(locale?: string, currency?: string): string;
        toCashAmount2(locale?: string, currency?: string): string;
        toCashAmount3(locale?: string, currency?: string): string;
        numberTuple(): ["+" | "-" | "", string];
    }

    interface Date {
        toCalendarFormat(): string;
        toFormattedString(): string;
        timeAgo(): string;
        toMMDDYYYY(): string;
        toHHMM(): string;
        countdown(): [days: number, hours: number, minutes: number];
    }

    interface Array<T> {
        joinWithAnd(locale?: string, options?: Intl.ListFormatOptions): string;
    }
}

Array.prototype.joinWithAnd = function (
    locale = "en",
    options: Intl.ListFormatOptions = {}
): string {
    const formatter = new Intl.ListFormat(locale, {
        style: "long",
        type: "conjunction",
        ...options
    });

    return formatter.format(this.map(String));
};

String.prototype.capitalize = function (): string {
    if (!this) return "";
    return this.charAt(0).toUpperCase() + this.slice(1);
};

String.prototype.titleCase = function (): string {
    return this.toLowerCase()
        .split(" ")
        .map((word) => word.capitalize())
        .join(" ");
};

String.prototype.truncate = function (start = 10, end = 10): string {
    return `${this.slice(0, start)}...${this.slice(-end)}`;
};

String.prototype.truncate2 = function (limit: number): string {
    const str = this.toString();
    if (str.length <= limit) return str;
    if (limit <= 3) return str.slice(0, limit);

    const ellipsis = "...";
    const keep = limit - ellipsis.length;
    const start = Math.ceil(keep / 2);
    const end = Math.floor(keep / 2);

    return str.slice(0, start) + ellipsis + str.slice(str.length - end);
};

String.prototype.isNumeric = function (): boolean {
    return this.trim() !== "" && !isNaN(Number(this));
};

String.prototype.toCents = function (): number {
    const normalized = this.replace(/,/g, "").trim();
    const n = Number.parseFloat(normalized);

    if (!Number.isFinite(n)) return 0;

    return Math.round(n * 100);
};

Number.prototype.toCashAmount = function (
    locale: string = "en-US",
    currency: string = "USD"
): string {
    const amount = this.valueOf() / 100;
    const isWholeDollar = Number.isInteger(amount);

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: isWholeDollar ? 0 : 2,
        maximumFractionDigits: 2
    }).format(amount);
};

Number.prototype.toCashAmount2 = function (
    locale: string = "en-US",
    currency: string = "USD"
): string {
    const amount = this.valueOf() / 100;

    return new Intl.NumberFormat(locale, {
        style: "decimal",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

Number.prototype.toCashAmount3 = function (
    locale: string = "en-US",
    currency: string = "USD"
): string {
    const amount = this.valueOf() / 100;

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

Number.prototype.numberTuple = function () {
    const n = this.valueOf();

    if (n === 0) {
        return ["", "Zero"];
    }

    if (n > 0) {
        return ["+", n.toString()];
    }

    return ["-", Math.abs(n).toString()];
};

Date.prototype.toMMDDYYYY = function (): string {
    return new Intl.DateTimeFormat("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
    }).format(this);
};

Date.prototype.toHHMM = function (): string {
    return new Intl.DateTimeFormat("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
    }).format(this);
};

Date.prototype.countdown = function (): [number, number, number] {
    const now = Date.now();
    const target = this.getTime();

    let diff = Math.max(0, target - now);

    const totalMinutes = Math.floor(diff / 1000 / 60);

    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    return [days, hours, minutes];
};

// not used bc same as: date.toLocaleDateString("en-CA"); // trick to get format YYYY-MM-DD // both are buggy for some reason
Date.prototype.toCalendarFormat = function (): string {
    const month = (this.getMonth() + 1).toString().padStart(2, "0");
    const day = this.getDate().toString().padStart(2, "0");
    const year = this.getFullYear().toString();
    return `${year}-${month}-${day}`;
};

Date.prototype.toFormattedString = function (): string {
    const options: Intl.DateTimeFormatOptions = {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: "America/Los_Angeles", // TODO: later change to match user's timezone
        timeZoneName: "short"
    };

    const formatter = new Intl.DateTimeFormat("en-US", options); // TODO: change to user's lang
    const formatted = formatter.format(this);

    // Replace comma after year with " at" and adjust spacing
    return formatted.replace(/, (\d{1,2}:\d{2} [AP]M)/, " at$1");
};

Date.prototype.timeAgo = function (): string {
    const now = new Date();
    const seconds = Math.floor((now.getTime() - this.getTime()) / 1000);

    if (seconds < 0) return "in the future";

    const intervals = [
        { seconds: 31536000, name: "year" },
        { seconds: 2592000, name: "month" },
        { seconds: 604800, name: "week" },
        { seconds: 86400, name: "day" },
        { seconds: 3600, name: "hour" },
        { seconds: 60, name: "minute" },
        { seconds: 1, name: "second" }
    ];

    for (const interval of intervals) {
        if (seconds >= interval.seconds) {
            const value = Math.floor(seconds / interval.seconds);
            return `${value} ${interval.name}${value !== 1 ? "s" : ""} ago`;
        }
    }

    return "just now";
};

export const isProduction = process.env.NODE_ENV === "production";

// same as backend
export const HB = isProduction
    ? {
          //safe: "", // not used here; only backend
          vault: "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAq4g6_m0O_Fk8WZb7oDqfH0rGl4H9mdeseiznChkMLUv0rRBbzLgrjmltXpB-jGfPPKO0vmxT8T_POr5uq8hFGmjhRC84P4-cdjhebGmmwGlz6JiHf4rRQp_WbQDCgrIzcjxifkrOvsaJ-SNopIfWsZRiD1gm6WtUfCSpPyu4X5aZxmg2RTPsfNpuQPcdy7FvUaiL-fBZk40ipA1RUUgVbLHWpnq12OvUF6HYEfSorj9ZHDIVnpKD8jZXG-9x65LZgU661SfpDVeBQ5mRBcxt_AXn0NeIGbnID2Nd4zBOrDc9YX2HbA3p7pqDIfcOg6oimhLmgVPjPgtjxY_E5ytL5QIDAQAB",
          trove: "",
          treasury: "",
          warehouse: "",
          aquarius: "",
          franchise: ""
      }
    : {
          //safe: "", // not used here; only backend
          vault: "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAhsZgTqeJnnVfB2lNeZngrNkRqGggK4HsgeX7vnVJAxFhd8qd8TAcraBOGXLS5axr4ujeU_Aj495vhNMNmDY46nrWSNFcDWF87w8JzFvo4ZWvpQCwYvjoATVRFPQ_WX-rhXERyhujfiVyzhQwNPymFNxZKYzs_ux82mNly56IBxKLTLflxq-CdmagGIBBQaJa4rDNywTlvGNNpsVq6uChaiGIh-a-uhxahnpUE8c2Nf0meX7f0XTZdj-VLDE-CyZ4ER0TxpseDxldF5iYoQzDfbOdh-J5n8r5G8rtMPGW8OSpxV--ZdjFDj6q56WqvHA1AxktYEkLJLaG0yGewWQtlQIDAQAB",
          trove: "",
          treasury: "",
          warehouse: "",
          aquarius: "",
          franchise: ""
      };

export const ADMINS = ["thonly@heartbank.ceo"];
export const BANKERS = ["thonly@heartbank.ceo", "sely0928@gmail.com"]; // family tellers
export const MONKS = ["thonly@heartbank.ceo", "panhiathao23@gmail.com"];
export const B = isProduction ? "https://id.333.eco" : "http://127.0.0.1:3000";
export const APP = isProduction
    ? "https://vault.333.eco"
    : "http://127.0.0.1:1234";

export const COLLECTION =
    "https://raw.githubusercontent.com/333eco/stock.333.eco/main/collection";
export const VAULT =
    "https://raw.githubusercontent.com/333eco/stock.333.eco/main/vault";
export const TREASURY =
    "https://raw.githubusercontent.com/333eco/stock.333.eco/main/treasury";
export const TROVE =
    "https://raw.githubusercontent.com/333eco/stock.333.eco/main/trove";
export const PIXXIKAT =
    "https://raw.githubusercontent.com/333eco/stock.333.eco/main/pixxikat";

const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
const isIOSStandalone = (navigator as any).standalone === true; // iOS-specific
export const isPwa = isStandalone || isIOSStandalone;
export const isUSDZ = isProduction ? isApple : osName === "iOS";

const params = new URLSearchParams(window.location.search);
export const isPopup = params.has("origin");
export const isRunningAsPWA = params.get("origin") === "pwa" || isPwa;

export const opener = window.opener as Window | null; //opener: Window | null = window.opener;
export const isInIframe: boolean = window.self !== window.top; //window !== window.parent
export const iframe = window.parent;

export type Choice = "public" | "anonymous" | "shuffle" | "remember";
export const defaults = {
    checkin: false, // isPrivate
    ar: false,
    locker: "remember" as Choice, // logged out: no public
    safe: "remember" as Choice,
    remember: "public" // or "anonymous" // for both locker and safe
};

export function getDeviceID(): string {
    let deviceID = localStorage.getItem("device");
    if (!deviceID) {
        deviceID = window.crypto.randomUUID();
        localStorage.setItem("device", deviceID);
    }
    return deviceID;
}

let IP = "";
export async function getIP() {
    try {
        if (!IP) {
            const res = await fetch("https://api.ipify.org?format=json");
            const { ip } = await res.json();
            IP = ip;
        }
    } catch (error) {
        console.error(error);
    } finally {
        //console.log(IP);
        return IP;
    }
}
let location: any = {};
export async function setIpLocation(ip: string) {
    try {
        if (location.ip !== ip) {
            const geoRes = await fetch(`https://ipapi.co/${ip}/json/`);
            location = await geoRes.json();
        }
    } catch (error) {
        //console.error(error);
    } finally {
        location.ip = ip;
        //console.log(location);
        localStorage.setItem("ip-location", JSON.stringify(location));
    }
}
export function getIpLocation() {
    return JSON.parse(localStorage.getItem("ip-location") || "{}");
}

// place location
export function getLocation() {
    return JSON.parse(localStorage.getItem("location") || "{}");
}

////

export function getHero() {
    return JSON.parse(localStorage.getItem("hero") || "{}");
}

function getLanguageFromIp() {
    const location = getIpLocation();
    switch (location.country) {
        case "US":
            return "en";
        default:
            return "en";
    }
}

export function getLanguage(hero: any = null) {
    if (!hero) hero = getHero();
    return params.get("lang") || hero.languageCode || getLanguageFromIp();
}

export function getAddress(hero: any = null) {
    if (!hero) hero = getHero();

    const location = getIpLocation();
    const region =
        location.city && location.region
            ? `${location.city}, ${location.region}`
            : "";

    return hero.address || region || window.earth.longBeach.region;
}

export function getPosition(hero: any = null): { lat: number; lng: number } {
    if (!hero) hero = getHero();
    return {
        lat: hero.latitude || window.earth.longBeach.lat,
        lng: hero.longitude || window.earth.longBeach.lng
    };
}

export function isTip(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.isTip || false;
}

export function isBroadcast(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.isBroadcast || false;
}

export function isSos(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.isSos || false;
}

export function isSidekick(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.isSidekick || false;
}

export function isRecord(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.isRecord || false;
}

export function getUsername(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.username ? "@" + hero.username : null;
}

export function getPrivateName(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.displayName || getUsername(hero) || hero.email;
}

export function getPublicName(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.displayName || getUsername(hero);
}

export function getName(hero: any = null, isHeartkey: boolean = true) {
    if (!hero) hero = getHero();
    return (
        getPublicName(hero) ||
        (isHeartkey
            ? hero.heartkey?.truncate(5, 5)
            : hero.trustkey?.truncate(5, 5)) ||
        "No Name"
    );
}

export function getSafe(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.heartkey || null;
}

export function getLocker(hero: any = null) {
    if (!hero) hero = getHero();
    return hero.trustkey || null;
}

export function getRandomAvatarUrl(): string {
    return `https://raw.githubusercontent.com/SiliconWat/avatar.siliconwat.dev/main/id/AV${getRandomNumber(
        1,
        120
    )}.png`;
}

export function getRandomNumber(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export async function pickFile(options?: {
    accept?: string;
    capture?: "microphone" | "camera" | "camcorder";
}): Promise<File> {
    const files = options ? await pickFiles(options) : await pickFiles();
    return files[0];
}

// used in step 1 diamonds
export async function pickFiles(options?: {
    accept?: string;
    capture?: "microphone" | "camera" | "camcorder";
    multiple?: boolean;
}): Promise<File[]> {
    return new Promise((resolve, reject) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = options?.accept || "image/*";
        input.style.display = "none"; // Hide the input
        if (options?.capture) input.setAttribute("capture", options.capture);
        if (options?.multiple) input.multiple = true;
        document.body.appendChild(input);

        const cleanup = () => {
            clearTimeout(timeoutId);
            window.removeEventListener("focus", onFocus);
            document.body.removeChild(input);
        };

        input.addEventListener("change", () => {
            cleanup();
            if (input.files && input.files.length > 0) {
                resolve(Array.from(input.files));
            } else {
                reject(new Error("No file selected"));
            }
        });

        const onFocus = () => {
            setTimeout(() => {
                if (!input.files || input.files.length === 0) {
                    cleanup();
                    reject(new Error("cancelled"));
                }
            }, 500);
        };

        window.addEventListener("focus", onFocus);

        const timeoutId = setTimeout(() => {
            cleanup();
            reject(new Error("File selection timed out"));
        }, 60000); // 60 seconds

        input.click();
    });
}

export async function fetchBlob(url: string) {
    console.log(url);
    const response = await fetch(url, { mode: "cors" });
    return await response.blob();
}

export async function downloadFile(name: string, data: string | Blob) {
    const blob = typeof data === "string" ? await fetchBlob(data) : data; // Fetch the file as a blob so we can force download
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();

    // Clean up
    // URL.revokeObjectURL(a.href);
    // document.body.removeChild(a);
    setTimeout(() => {
        URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
    }, 100);
}

export function loadYouTube(component: HTMLElement) {
    component
        .querySelectorAll<HTMLIFrameElement>("iframe[data-src]")
        .forEach((iframe) => {
            const dataSrc = iframe.dataset.src;
            if (dataSrc) {
                iframe.src = dataSrc;
                iframe.removeAttribute("data-src");
            }
        });
}

export function loadYouTubeAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!navigator.onLine || (window as any).YT?.Player) {
            resolve();
        }

        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";
        script.async = true;

        script.onload = () => {
            (window as any).onYouTubeIframeAPIReady = () => {
                resolve();
            };
        };

        script.onerror = () => {
            reject(new Error("Failed to load YouTube IFrame API"));
        };

        document.head.appendChild(script);
    });
}

export function wait(seconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, seconds * 1000));
}

/**
 * Convert URLs in a message string into anchor tags:
 * e.g. "visit https://example.com/foo" -> "visit <a href="https://example.com/foo">example.com</a>"
 */
export function linkify(message: string): string {
    // Regex matches either http(s)://... or www.... (stops at whitespace)
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

    // Escape text for HTML (to prevent XSS)
    const escapeHtml = (str: string) =>
        str
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    // Escape attribute value
    const escapeAttr = (str: string) =>
        str
            .replace(/&/g, "&amp;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

    let result = "";
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // Use exec loop to preserve text between matches and safely build output
    while ((match = urlRegex.exec(message)) !== null) {
        const start = match.index;
        const matchedText = match[0];

        // push escaped text before the match
        result += escapeHtml(message.slice(lastIndex, start));

        // determine href: if it starts with www., prepend http:// so it's clickable
        const href = matchedText.startsWith("www.")
            ? `http://${matchedText}`
            : matchedText;

        // obtain domain name for display (hostname)
        let displayDomain = matchedText;
        try {
            // Use URL for robust parsing; if missing scheme, add http://
            const urlForParse = matchedText.startsWith("http")
                ? matchedText
                : `http://${matchedText}`;
            const parsed = new URL(urlForParse);
            displayDomain = parsed.hostname;
            // strip leading "www." for display if present
            if (displayDomain.toLowerCase().startsWith("www.")) {
                displayDomain = displayDomain.slice(4);
            }
        } catch {
            // fallback: strip path/query using simple regex
            displayDomain = matchedText
                .replace(/^https?:\/\//i, "")
                .replace(/\/.*$/, "");
            if (displayDomain.toLowerCase().startsWith("www."))
                displayDomain = displayDomain.slice(4);
        }

        // build anchor HTML (note: following user's requested format)
        result += `<a href="${escapeAttr(
            href
        )}" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline">${escapeHtml(
            displayDomain
        )}</a>`;

        lastIndex = urlRegex.lastIndex;
    }

    // append any remaining escaped text
    result += escapeHtml(message.slice(lastIndex));
    return result.replaceAll("\n", "<br>");
}

// for speech
export function unlinkify(message: string): string {
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return message.replace(urlRegex, (url) => {
        try {
            const { hostname } = new URL(url);
            return hostname.replace(/^www\./, ""); // remove 'www.' if present
        } catch {
            return url; // if not a valid URL, return as is
        }
    });
}

/**
 * Parses a string to check for a valid YouTube video ID.
 * Returns the ID and a short URL if found, otherwise returns null.
 */
export function parseYouTubeUrl(url: string): {
    videoId: string;
    shortUrl: string;
} | null {
    // Regex Breakdown:
    // 1. ^(?:https?:\/\/)?           -> Optional protocol (http or https)
    // 2. (?:www\.|m\.)?              -> Optional subdomains (www or m)
    // 3. (?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))
    //    -> Checks for youtu.be OR youtube.com followed by specific paths (embed, v, watch, shorts)
    // 4. ([\w-]{11})                 -> CAPTURE GROUP 1: The 11-character video ID (alphanumeric + _ -)
    // 5. (?:(?:[?&]|\/).*)?$         -> Ignore trailing characters (query params like &t=12s)
    const regExp =
        /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([\w-]{11})(?:(?:[?&]|\/).*)?$/;

    const match = url.match(regExp);

    if (match && match[1]) {
        const videoId = match[1];

        return {
            videoId: videoId,
            shortUrl: `https://youtu.be/${videoId}`
        };
    }

    return null;
}

export function redirect(page: string, isHome = false) {
    if (localStorage.getItem("redirect-" + page)) {
        window.location.hash = localStorage.getItem("redirect-" + page)!;
        localStorage.removeItem("redirect-" + page);
    } else if (isHome) window.location.hash = "#";
}

export function enableTapAndLongPress(el: HTMLElement, delay = 300) {
    let timer: number;
    let longPress = false;

    el.addEventListener("pointerdown", () => {
        longPress = false;

        timer = window.setTimeout(() => {
            longPress = true;
            el.dispatchEvent(new CustomEvent("longpress"));
        }, delay);
    });

    el.addEventListener("pointerup", () => {
        clearTimeout(timer);
        if (!longPress) {
            el.dispatchEvent(new CustomEvent("tap"));
        }
    });

    ["pointerleave", "pointercancel"].forEach((e) =>
        el.addEventListener(e, () => clearTimeout(timer))
    );

    ///

    el.addEventListener("contextmenu", (e) => e.preventDefault());

    // let startX = 0,
    //     startY = 0;

    // el.addEventListener("pointerdown", (e) => {
    //     startX = e.clientX;
    //     startY = e.clientY;
    // });

    // el.addEventListener("pointermove", (e) => {
    //     if (Math.hypot(e.clientX - startX, e.clientY - startY) > 10) {
    //         clearTimeout(timer);
    //     }
    // });
}

////

type Thank = "donate" | "tip" | "retip";
export const REWARD_SHARE = 0.5; // reward share between b-account and b-fund
export const SENDER_SHARE = 0.5; // sender sharing percentage to family b-kitty
export const RECEIVER_SHARE = 0.5; // receiver sharing percentage to Retip B-Fund
const DONATION_SHARE = 1 - SENDER_SHARE;
const GRATUITY_SHARE = 1 - SENDER_SHARE;

const TIP = "‼️ Enter 0 or leave blank to give Kiitos only\n\n"; // thank
const TIP2 = "‼️ Enter 0 or leave blank to give Kiitti only\n\n"; // thank all
const NOTE = `NOTE:\n\n• For your convenience, Miss Aquarius™ AI has provided a recommended tip amount based on your recipient's Aura and the context of this kindness and your gratitude`;
const NOTE0 = `\n\n• 100% of your tip will be added to your recipient's Personal B-Account for them to withdraw or re-tip as they wish`;
const NOTE1 = `\n\n• ${SENDER_SHARE * 100}% will be donated to your recipient's Retip B-Fund, and ${DONATION_SHARE * 100}% will be automatically contributed to your Family B-Kitty to help nurture a Culture of Gratitude and Kindness among your family members`;
const NOTE2 = `\n\n• ${RECEIVER_SHARE * 100}% will be added to your recipient's Personal B-Account, and ${GRATUITY_SHARE * 100}% will be automatically donated to your recipient's Retip B-Fund to empower them to carry your generosity forward to the people around them`;
const NOTE3 = `\n\n• ${SENDER_SHARE * 100}% will be donated to the sender's Retip B-Fund, and ${DONATION_SHARE * 100}% will be automatically contributed to your Family B-Kitty to help nurture a Culture of Gratitude and Kindness among your family members\n\n• ${RECEIVER_SHARE * 100}% will be added to the receiver's Personal B-Account, and ${GRATUITY_SHARE * 100}% will be automatically donated to the receiver's Retip B-Fund to empower them to carry your generosity forward to the people around them`;

//TODO: can show calculated amount but can do later bc here is not great and 50% is easy to understand // if do: update TCG
function getNote(isThank: boolean | null, isShow = true): string {
    if (isThank === null) return NOTE + (isShow ? NOTE3 : NOTE0);
    return NOTE + (isThank ? (isShow ? NOTE1 : NOTE0) : isShow ? NOTE2 : NOTE0);
}

// for thanking / rethanking / donating
export function getTipAmount(
    type: Thank,
    isRequired: boolean,
    isThank: boolean | null,
    isAnonymous: boolean,
    to: any,
    name: string,
    token: string,
    kiitos?: number, // TODO: later bring in kiitos algo from backend
    isShow = true,
    showAlert = isTip()
): number {
    const from = getHero();
    const fund = from.fund || 0;
    const account = from.account || 0;
    const isAccount = fund === 0;
    const balance = fund || account;

    const auraColor =
        ((to.kiitosFrequency || 0) + (to.kiittiFrequency || 0)) / 2;
    const auraSize =
        ((to.kiitosAmplitude || 0) + (to.kiittiAmplitude || 0)) / 2;

    if (isRequired && balance === 0)
        alert(
            "Sorry, you currently don't have any money in your HeartBank® Personal Account. Please make a deposit with your Family Banker."
        );
    else if (balance > 0) {
        if (
            !showAlert ||
            confirm(
                `Would you like to also ${isAnonymous ? "anonymously" : "publicly"} ${isThank === null ? "donate and tip" : isThank ? "donate to" : "tip"} ${name} from your HeartBank® ${isAccount ? "Personal Account" : "Retip Fund"}?`
            )
        ) {
            const tip = prompt(
                `🩷 How much would you like to ${isAnonymous ? "anonymously" : "publicly"} ${isThank === null ? "donate and tip" : isThank ? "donate to" : "tip"} ${name} from your HeartBank® ${isAccount ? "Personal Account" : "Retip Fund"}?\n\n🏦 Your Current ${isAccount ? "B-Account" : "B-Fund"} Balance: ${balance.toCashAmount3()}\n\n${(isRequired ? "" : TIP) + getNote(isThank, isShow)}`,
                kiitos
                    ? getRecommendedTipAmount2(
                          token,
                          balance,
                          auraColor,
                          auraSize,
                          kiitos,
                          0
                      )
                    : getRecommendedTipAmount(
                          type,
                          token,
                          balance,
                          auraColor,
                          auraSize
                      )
            );
            //if (tip === null) return -1;
            if (tip) {
                if (tip.isNumeric()) {
                    const cents = tip.toCents();
                    if (isRequired && cents === 0) return -1;
                    else if (cents <= balance) return cents;
                    else {
                        alert(
                            `Tip amount cannot be more than ${balance.toCashAmount3()}`
                        );
                        getTipAmount(
                            type,
                            isRequired,
                            isThank,
                            isAnonymous,
                            to,
                            name,
                            token,
                            kiitos,
                            isShow,
                            showAlert
                        );
                    }
                } else {
                    alert("Please enter a dollar amount");
                    getTipAmount(
                        type,
                        isRequired,
                        isThank,
                        isAnonymous,
                        to,
                        name,
                        token,
                        kiitos,
                        isShow,
                        showAlert
                    );
                }
            }
        }
    }

    return isRequired ? -1 : 0;
}

export function getRecommendedTipAmount(
    type: Thank,
    token: string,
    balance: number, // cents
    auraColor: number, // frequency: 0 - 100
    auraSize: number // amplitutde: kiitos
): string {
    const tip = localStorage.getItem("tip");
    const amount = getTokenAmount(token) + auraColor + auraSize; // cents
    return tip
        ? parseInt(tip).toCashAmount2()
        : Math.min(
              amount + getBaseTipAmount(type),
              // balance,
              balance / (isTip() ? getDaysLeftInCurrentMonth() : 1)
          ).toCashAmount2();
}

// for re-thanking
export function getTipAmount2(
    isThank: boolean | null,
    isAnonymous: boolean,
    to: any,
    name: string,
    token: string,
    kiitos: number,
    kiitti: number = 0,
    showAlert = !isTip()
): number {
    const from = getHero();
    const fund = from.fund || 0;
    const account = from.account || 0;
    const isAccount = fund === 0;
    const balance = fund || account;

    const auraColor =
        ((to.kiitosFrequency || 0) + (to.kiittiFrequency || 0)) / 2;
    const auraSize =
        ((to.kiitosAmplitude || 0) + (to.kiittiAmplitude || 0)) / 2;

    if (balance > 0) {
        if (
            !showAlert ||
            confirm(
                `Would you like to also ${isAnonymous ? "anonymously" : "publicly"} ${isThank === null ? "donate and tip" : isThank ? "donate to" : "tip"} ${name} from your HeartBank® ${isAccount ? "Personal Account" : "Retip Fund"}?`
            )
        ) {
            const tip = prompt(
                `🩷 How much would you like to ${isAnonymous ? "anonymously" : "publicly"} ${isThank === null ? "donate and tip" : isThank ? "donate to" : "tip"} ${name}?\n\n🏦 Your Current ${isAccount ? "B-Account" : "B-Fund"} Balance: ${balance.toCashAmount3()}\n\n${isTip() ? TIP : ""}${getNote(isThank)}`,
                getRecommendedTipAmount2(
                    token,
                    balance,
                    auraColor,
                    auraSize,
                    kiitos,
                    kiitti
                )
            );
            if (tip === null) return -1;
            if (tip) {
                if (tip.isNumeric()) {
                    const cents = tip.toCents();
                    if (cents <= balance) {
                        return cents;
                    } else {
                        alert(
                            `Tip amount cannot be more than ${balance.toCashAmount3()}`
                        );
                        getTipAmount2(
                            isThank,
                            isAnonymous,
                            to,
                            name,
                            token,
                            kiitos,
                            kiitti,
                            false
                        );
                    }
                } else {
                    alert("Please enter a dollar amount");
                    getTipAmount2(
                        isThank,
                        isAnonymous,
                        to,
                        name,
                        token,
                        kiitos,
                        kiitti,
                        false
                    );
                }
            }
        }
    }

    return 0;
}

function getRecommendedTipAmount2(
    token: string,
    balance: number, // cents
    auraColor: number, // frequency: 0 - 100
    auraSize: number, // amplitutde: kiitos
    kiitos: number,
    kiitti: number
): string {
    const tip = localStorage.getItem("tip");
    const amount =
        getTokenAmount(token) + auraColor + auraSize + kiitos + kiitti; // cents
    return tip
        ? parseInt(tip).toCashAmount2()
        : Math.min(
              amount + getBaseTipAmount("retip"),
              // balance,
              balance / (isTip() ? getDaysLeftInCurrentMonth() : 1)
          ).toCashAmount2();
}

// for re-thanking all // no aura for now
export function getTipAmount3(
    total: number, // total number of people
    token: string,
    kiitos: number,
    kiitti: number,
    showAlert = !isTip()
): number {
    const from = getHero();
    const fund = from.fund || 0;
    const account = from.account || 0;
    const isAccount = fund === 0;
    const balance = fund || account;

    if (total > 0 && balance > 0) {
        if (
            !showAlert ||
            confirm(
                `Would you like to also anonymously tip everyone (${total} people total) from your HeartBank® ${isAccount ? "Personal Account" : "Retip Fund"}?`
            )
        ) {
            const tip = prompt(
                `🩷 How much would you like to anonymously tip each person?\n\n🏦 Your Current ${isAccount ? "B-Account" : "B-Fund"} Balance: ${balance.toCashAmount3()}\n\n${isTip() ? TIP2 : ""}${getNote(null)}`,
                getRecommendedTipAmount3(total, token, balance, kiitos, kiitti)
            );
            if (tip === null) return -1;
            if (tip) {
                if (tip.isNumeric()) {
                    const cents = tip.toCents();
                    if (cents * total <= balance) {
                        return cents;
                    } else {
                        alert(
                            `Tip amount cannot be more than ${(balance / total).toCashAmount3()}`
                        );
                        getTipAmount3(total, token, kiitos, kiitti, false);
                    }
                } else {
                    alert("Please enter a dollar amount");
                    getTipAmount3(total, token, kiitos, kiitti, false);
                }
            }
        }
    }

    return 0;
}

function getRecommendedTipAmount3(
    total: number, // total number of people
    token: string,
    balance: number, // cents
    kiitos: number,
    kiitti: number
): string {
    const tip = localStorage.getItem("tip");
    const amount = getTokenAmount(token) + kiitos + kiitti; // cents
    return tip
        ? parseInt(tip).toCashAmount2()
        : Math.min(
              (amount + getBaseTipAmount("retip")) / total,
              // balance / total,
              balance / total / (isTip() ? getDaysLeftInCurrentMonth() : 1)
          ).toCashAmount2();
}

function getBaseTipAmount(type: Thank): number {
    switch (type) {
        case "donate": // MAX
            return 1000; // 10 figure dollars
        case "tip":
            return 100; // 1 figure dollars
        case "retip": // MIN
            return 10; // 10 figure cents
        default: // not used
            return 1; // 1 figure cents
    }
}

function getDaysLeftInCurrentMonth(): number {
    const now = new Date();

    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based (0 = January)

    // Last day of current month
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    return lastDayOfMonth - now.getDate() + 1; // add 1 to include today; needed to prevent divide by zero error on last day of month
}

function getTokenAmount(token: string): number {
    const multiplier = 3;
    switch (token) {
        case "violet":
            return 7 + multiplier;
        case "indigo":
            return 6 + multiplier;
        case "blue":
            return 5 + multiplier;
        case "green":
            return 4 + multiplier;
        case "yellow":
            return 3 + multiplier;
        case "orange":
            return 2 + multiplier;
        case "red":
            return 1 + multiplier;
        default:
            return 0;
    }
}

/////

export function getCircle(kiitos: number, isKiitos?: boolean) {
    const circle =
        isKiitos === true
            ? "INNER CIRCLE"
            : isKiitos === false
              ? "OUTER CIRCLE"
              : "EARTH";

    if (kiitos === 0) return `⭕️ ${circle}:`;
    if (kiitos > 0) return `🔺 ${circle}:`;
    return `🔻 ${circle}:`;
}

export function getKiitosSummary(kiitos: number, name = "") {
    const adverb = getAdverb(kiitos);
    if (kiitos === 0)
        return "which means giving and receiving have returned to balance";
    else if (kiitos > 0)
        return `which means ${
            name ? `${name} has` : "you have"
        } been ${adverb}kind towards people nearby`;
    else
        return `which means ${
            name ? `${name} has` : "you have"
        } been ${adverb}appreciative towards people nearby`;
}

export function getKiittiSummary(kiitti: number, name = "") {
    const footer = name === "Miss Aquarius™" ? "humanity" : "all of life";
    const adverb = getAdverb(kiitti);
    if (kiitti === 0)
        return "which means giving and receiving have returned to balance";
    else if (kiitti > 0)
        return `which means ${
            name ? `${name} has` : "you have"
        } been ${adverb}kind towards ${footer}`;
    else
        return `which means ${
            name ? `${name} has` : "you have"
        } been ${adverb}appreciative towards ${footer}`;
}

export function getAdverb(kiitos: number) {
    const value = Math.abs(kiitos);
    if (value > 0 && value <= 100) return "";
    else if (value > 100 && value <= 500) return "especially ";
    else return "extremely ";
}

export const TOKENS = [
    {
        token: "red",
        emoji: "❤️",
        color: "bg-red-500"
    },
    { token: "orange", emoji: "🧡", color: "bg-orange-500" },
    { token: "yellow", emoji: "💛", color: "bg-yellow-500" },
    { token: "green", emoji: "💚", color: "bg-green-500" },
    { token: "blue", emoji: "🩵", color: "bg-blue-500" },
    { token: "indigo", emoji: "🩷", color: "bg-pink-500" },
    { token: "violet", emoji: "💜", color: "bg-purple-500" }
];

//// AURA

export const ringColors = [
    // Reds
    "ring-red-400",
    "ring-red-500",
    "ring-rose-400",
    "ring-rose-500",

    // Oranges / Ambers
    "ring-orange-400",
    "ring-orange-500",
    "ring-amber-400",
    "ring-amber-500",

    // Yellows / Limes
    "ring-yellow-400",
    "ring-yellow-500",
    "ring-lime-400",
    "ring-lime-500",

    // Greens
    "ring-green-400",
    "ring-green-500",
    "ring-emerald-400",
    "ring-emerald-500",
    "ring-teal-400",
    "ring-teal-500",

    // Cyans / Blues
    "ring-cyan-400",
    "ring-cyan-500",
    "ring-sky-400",
    "ring-sky-500",
    "ring-blue-400",
    "ring-blue-500",
    "ring-indigo-400",
    "ring-indigo-500",

    // Purples / Violets
    "ring-violet-400",
    "ring-violet-500",
    "ring-purple-400",
    "ring-purple-500",
    "ring-fuchsia-400",
    "ring-fuchsia-500",

    // Pinks
    "ring-pink-400",
    "ring-pink-500"

    // Neutrals (subtle but useful)
    // "ring-slate-400",
    // "ring-gray-400",
    // "ring-zinc-400",
    // "ring-neutral-400",
    // "ring-stone-400"
];

// for background
export function getAuraColor(kiitos: number, kiitti: number): string {
    // Clamp score to 0–100
    const s = Math.max(0, Math.min(100, (kiitos + kiitti) / 2));

    // Map score to hue (0 → 270)
    const hue = (s / 100) * 270;

    // Return HSL color
    //return `hsla(${hue},100%,50%,0.5)`; // conflicts with beating heart animation color
    return `hsl(${hue},100%,50%)`;
}

export function getKiitosAuraColor(kiitos: number): string {
    // Clamp score to 0–100
    const s = Math.max(0, Math.min(100, kiitos));

    // Map score to hue (0 → 270)
    const hue = (s / 100) * 270;
    //const hue = Math.floor(Math.random() * 271);

    // Return HSL color
    return `hsl(${hue},100%,50%)`;
}

export function getKiittiAuraColor(kiitti: number): string {
    // Clamp score to 0–100
    const s = Math.max(0, Math.min(100, kiitti));

    // Map score to hue (0 → 270)
    const hue = (s / 100) * 270;
    //const hue = Math.floor(Math.random() * 271);

    // Return HSL color
    return `hsl(${hue},100%,50%)`;
}

// doesn't work
export function getKiittiAuraColor2(kiitti: number): string {
    const s = Math.max(0, Math.min(100, kiitti));
    const h = (s / 100) * 270;

    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const c = 0.5 - Math.max(Math.min(k - 3, 9 - k, 1), -1) / 2;
        return Math.round(255 * c)
            .toString(16)
            .padStart(2, "0");
    };

    return `ring-[#${f(0)}${f(8)}${f(4)}]`;
}

// not used
function getAuraSize(kiitos: number, kiitti: number): number {
    return Math.max(5, (kiitos + kiitti) / 2);
}

export function getKiitosAuraSize(
    kiitos: number,
    size?: "s" | "m" | "l"
): string {
    switch (size) {
        case "s":
            return 6 + "px";
        case "m":
            return 9 + "px";
        case "l":
            return 12 + "px";
        default:
            return 3 + "px";
    }
}

export function getKiittiAuraSize(
    kiitti: number,
    size?: "s" | "m" | "l"
): string {
    switch (size) {
        case "s":
            return 3 + "px";
        case "m":
            return 6 + "px";
        case "l":
            return 9 + "px";
        default:
            return 2 + "px";
    }
}

// doesn't work
export function getKiittiAuraSize2(
    kiitti: number,
    size?: "s" | "m" | "l"
): string {
    switch (size) {
        case "s":
            return "ring-[3px]";
        case "m":
            return "ring-[6px]";
        case "l":
            return "ring-[9px]";
        default:
            return "ring-[2px]";
    }
}

type RainbowColor =
    | "Red"
    | "Orange"
    | "Yellow"
    | "Green"
    | "Blue"
    | "Indigo"
    | "Violet";

export function getAuraName(frequency: number): RainbowColor {
    const colors: RainbowColor[] = [
        "Red",
        "Orange",
        "Yellow",
        "Green",
        "Blue",
        "Indigo",
        "Violet"
    ];

    const clamped = Math.max(0, Math.min(100, frequency));
    const index = Math.min(
        colors.length - 1,
        Math.floor((clamped / 100) * colors.length)
    );

    return colors[index];
}

export function getAuraRing(frequency: number, isKiitos?: boolean) {
    return `${getAuraEmoji(frequency)} ${isKiitos === true ? "INNER RING" : isKiitos === false ? "OUTER RING" : "EARTH"}:`;
}

export function getAuraEmoji(frequency: number) {
    const color = getAuraName(frequency);
    switch (color) {
        case "Red":
            return "🔴";
        case "Orange":
            return "🟠";
        case "Yellow":
            return "🟡";
        case "Green":
            return "🟢";
        case "Blue":
            return "🔵";
        case "Indigo":
            return "🟣";
        case "Violet":
            return "🟣";
    }
}

///

export function haversineMiles(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;

    const R = 3958.8; // Earth radius in miles

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
}
